import { DRIVER_STATUS } from '@allocab/allocab-commons-shared-types';
import { DriverWithStatusVehicleTripCountOutput } from '@allocab/allocab-driver-service-types';
import {
  CarSolid,
  ClockRotateLeftSolid,
  EnvelopeSolid,
  EyeSolid,
  MobileScreenButtonSolid,
  RouteSolid,
} from '@allocab/allocab-icons';

import { Link } from '../../components/atomic/Link';
import { BadgeIndicator } from '../../components/BadgeIndicator';
import { LicensePlate } from '../../components/LicensePlate';
import { t } from '../../services/translation/translation';
import { getFullName } from '../../utils/common';
import { formatDate } from '../../utils/date';
import { getStatusText } from '../../utils/driver';
import { getVehicleName } from '../../utils/vehicle';

type Props = {
  driver: DriverWithStatusVehicleTripCountOutput;
};

export const DriverCard = ({ driver }: Props) => {
  const status = driver.status as DRIVER_STATUS;
  return (
    <div className="max-w-full p-3 bg-white border border-gray-200 rounded-lg shadow dark:bg-gray-800 dark:border-gray-700 space-y-1">
      <div className="grid grid-cols-[1fr,1.5fr,0.5fr] gap-1">
        <div className="flex items-center justify-center gap-2  p-2 text-xs rounded-lg bg-gray-50 dark:bg-gray-600">
          <CarSolid className="fill-current" width={13} height={13} />
          <Link className="text-center" to={`/drivers/${driver.id}`}>
            {getFullName(driver.firstName, driver.lastName)}
          </Link>
        </div>
        <div className="flex items-center justify-center space-x-2 p-2 text-xs rounded-lg bg-gray-50 dark:bg-gray-600">
          <MobileScreenButtonSolid className="fill-current" width={13} height={13} />
          <div>{driver.phoneNumber}</div>
        </div>
        <div className="text-center">
          <BadgeIndicator
            variant={
              status === 'ACTIVATED'
                ? 'green'
                : status === 'DEACTIVATED' || status === 'SUSPENDED'
                ? 'red'
                : status === 'SUBSCRIPTION' || status === 'SUBSCRIPTION_REVIEW'
                ? 'yellow'
                : 'gray'
            }
          >
            {getStatusText(status)}
          </BadgeIndicator>
        </div>
      </div>
      <div className="grid grid-cols-[1.25fr,1.25fr,0.5fr] gap-1">
        <div className="flex items-center justify-center gap-2  p-2 text-xs rounded-lg bg-gray-50 dark:bg-gray-600">
          <EnvelopeSolid className="fill-current" width={13} height={13} />
          <div>{driver.email}</div>
        </div>
        <div className="flex items-center justify-center gap-2  p-2 text-xs rounded-lg bg-gray-50 dark:bg-gray-600">
          <ClockRotateLeftSolid className="fill-current" width={13} height={13} />
          <div className="flex text-center">
            {driver.statusUpdatedAt ? formatDate(driver.statusUpdatedAt, t('date.datetime')) : '--'}
          </div>
        </div>
        <div className="flex items-center justify-center gap-2  p-2 text-xs rounded-lg bg-gray-50 dark:bg-gray-600">
          {driver.params.canSeePrice ? (
            <EyeSolid width={16} height={16} className="fill-green-500" />
          ) : (
            <EyeSolid width={16} height={16} className="fill-red-500" />
          )}
        </div>
      </div>
      <div className="grid grid-cols-[0.5fr,1.25fr,1.25fr] gap-1">
        <div className="flex items-center justify-center  space-x-2 p-2 text-xs rounded-lg bg-gray-50 dark:bg-gray-600">
          <RouteSolid className="fill-current" width={13} height={13} />
          <div>{driver.tripCount}</div>
        </div>
        <div className="flex items-center justify-center  p-2 text-xs rounded-lg bg-gray-50 dark:bg-gray-600">
          <Link to={`/vehicles/${driver.vehicle?.id}`}>
            <LicensePlate plateNumber={driver.vehicle ? driver.vehicle.licensePlate : undefined} />
          </Link>
        </div>
        <div className="flex items-center justify-center  p-2 text-xs rounded-lg bg-gray-50 dark:bg-gray-600">
          {driver.vehicle ? (
            <Link to={`/vehicles/${driver.vehicle?.id}`}>
              <span className="capitalize">{getVehicleName(driver.vehicle).toLocaleLowerCase()}</span>
            </Link>
          ) : (
            <div> {t('common.unknown')} </div>
          )}
        </div>
      </div>
    </div>
  );
};
